/**
 * OroraTech PrimeVue Theme Preset
 * Based on the WFS primeVueConfig.ts — defines OT-specific primitive colors,
 * surface palette, and semantic tokens for both light and dark modes.
 */
import { definePreset } from '@primevue/themes';
import Aura from '@primevue/themes/aura';

export const OroraPreset = definePreset(Aura, {
  primitive: {
    'off-white': '#FAF9F7',
    'forest-deep': '#0C232B',
    'space-mint': {
      50: '#DBFAF8',
      100: '#B2F5F1',
      200: '#70EDE5',
      300: '#4CE8DE',
      400: '#12DED1',
      500: '#03B6AF',
      600: '#008A83',
      700: '#006A64',
      800: '#005C56',
      900: '#004A45',
    },
    'tech-flame': '#FF4724',
  },
  semantic: {
    primary: {
      50: '{space-mint.50}',
      100: '{space-mint.100}',
      200: '{space-mint.200}',
      300: '{space-mint.300}',
      400: '{space-mint.400}',
      500: '{space-mint.500}',
      600: '{space-mint.600}',
      700: '{space-mint.700}',
      800: '{space-mint.800}',
      900: '{space-mint.900}',
      950: '{space-mint.900}',
    },
    colorScheme: {
      light: {
        surface: {
          0: '#FAF9F7',
          50: '#F5F3F0',
          100: '#EBE8E4',
          200: '#D6DCDE',
          300: '#B1BBC0',
          400: '#8DA0A7',
          500: '#6F8790',
          600: '#536C76',
          700: '#233840',
          800: '#162D35',
          900: '#0C232B',
          950: '#0A1E24',
        },
        opacity: {
          dark: {
            10: 'rgba(0, 0, 0, 0.1)',
            80: 'rgba(0, 0, 0, 0.8)',
          },
        },
        mainGrid: {
          background: '{surface.200}',
        },
        mapFooter: {
          background: 'rgba(255, 255, 255, 0.6)',
          color: '{surface.950}',
        },
        mapInteractionButton: {
          background: '{surface.0}',
        },
        navbar: {
          background: '{surface.0}',
          color: '{surface.950}',
          button: {
            idle: { background: 'none', borderColor: 'transparent', color: '{surface.500}' },
            hover: { background: '{surface.100}', borderColor: '{surface.100}', color: '{surface.500}' },
            pressed: { background: '{surface.200}', borderColor: '{surface.200}', color: '{surface.500}' },
            active: {
              idle: { background: '{primary.100}', borderColor: '{primary.100}', color: '{primary.800}' },
              hover: { background: '{primary.200}', borderColor: '{primary.200}', color: '{primary.800}' },
              pressed: { background: '{primary.300}', borderColor: '{primary.300}', color: '{primary.800}' },
            },
          },
        },
        topbar: {
          borderColor: '{surface.200}',
        },
        scaleLine: {
          borderColor: '{surface.950}',
        },
        scrollbar: {
          background: '{surface.400}',
        },
        screenshot: {
          background: '{surface.0}',
        },
        slider: {
          background: '{surface.0}',
        },
        timeFilter: {
          buttonColor: '{surface.700}',
        },
        popup: {
          background: '{surface.0}',
          color: '{surface.950}',
        },
        toggleButton: {
          idle: { background: 'transparent', borderColor: 'transparent', color: '{surface.500}' },
          hover: { background: '{surface.100}', borderColor: '{surface.100}', color: '{surface.500}' },
          pressed: { background: '{surface.200}', borderColor: '{surface.200}', color: '{surface.500}' },
          active: {
            idle: { background: '{primary.100}', borderColor: '{primary.100}', color: '{primary.800}' },
            hover: { background: '{primary.200}', borderColor: '{primary.200}', color: '{primary.800}' },
            pressed: { background: '{primary.300}', borderColor: '{primary.300}', color: '{primary.800}' },
          },
        },
      },
      dark: {
        surface: {
          0: '#FAF9F7',
          50: '#F5F3F0',
          100: '#EBE8E4',
          200: '#D6DCDE',
          300: '#B1BBC0',
          400: '#8DA0A7',
          500: '#6F8790',
          600: '#536C76',
          700: '#233840',
          800: '#162D35',
          900: '#0C232B',
          950: '#0A1E24',
        },
        opacity: {
          dark: {
            10: 'rgba(0, 0, 0, 0.1)',
            80: 'rgba(0, 0, 0, 0.8)',
          },
        },
        mainGrid: {
          background: '{surface.700}',
        },
        mapFooter: {
          background: 'rgba(12, 35, 43, 0.6)',
          color: '{surface.0}',
        },
        mapInteractionButton: {
          background: '{surface.700}',
        },
        navbar: {
          background: '{surface.900}',
          color: '{surface.0}',
          button: {
            idle: { background: 'none', borderColor: 'transparent', color: '{surface.400}' },
            hover: { background: '{surface.800}', borderColor: '{surface.800}', color: '{surface.400}' },
            pressed: { background: '{surface.700}', borderColor: '{surface.700}', color: '{surface.400}' },
            active: {
              idle: { background: '{primary.800}', borderColor: '{primary.700}', color: '{primary.200}' },
              hover: { background: '{primary.700}', borderColor: '{primary.700}', color: '{primary.200}' },
              pressed: { background: '{primary.600}', borderColor: '{primary.700}', color: '{primary.200}' },
            },
          },
        },
        topbar: {
          borderColor: '{surface.700}',
        },
        scaleLine: {
          borderColor: '{surface.0}',
        },
        scrollbar: {
          background: '{surface.600}',
        },
        screenshot: {
          background: '{surface.700}',
        },
        slider: {
          background: '{surface.700}',
        },
        timeFilter: {
          buttonColor: '{surface.300}',
        },
        popup: {
          background: '{surface.800}',
          color: '{surface.0}',
        },
        toggleButton: {
          idle: { background: 'transparent', borderColor: 'transparent', color: '{surface.400}' },
          hover: { background: '{surface.800}', borderColor: '{surface.800}', color: '{surface.400}' },
          pressed: { background: '{surface.700}', borderColor: '{surface.700}', color: '{surface.400}' },
          active: {
            idle: { background: '{primary.800}', borderColor: '{primary.700}', color: '{primary.200}' },
            hover: { background: '{primary.700}', borderColor: '{primary.700}', color: '{primary.200}' },
            pressed: { background: '{primary.600}', borderColor: '{primary.700}', color: '{primary.200}' },
          },
        },
      },
    },
  },
  components: {
    menu: {
      item: {
        iconFocusColor: '{primary.500}',
        focusColor: '{primary.500}',
      },
    },
    tabs: {
      tab: {
        padding: '0.5rem 0.75rem',
      },
    },
  },
});
